import { Fragment, PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import compact from 'lodash/compact';
import get from 'lodash/get';
import map from 'lodash/map';
import split from 'lodash/split';
import { useFacilityMenuItems } from 'shared-feature-facility-menu';

import {
  useAccountGet,
  useWebPunchSession,
  WEB_PUNCH_LINK,
} from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import { OverlayLoader } from '@sbv-clients/ui-shared';

import { FacilityPicker } from '../layouts/facility-picker';

const getPrefix = (pathname: string) => get(split(pathname, '/'), [1]);

// Pages every signed-in user can open, whatever the facility menu has.
const MENU_EXEMPT_PATH_PREFIXES = ['contact-us', 'preferences'];
const Body = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { allMenuItems = [], isLoading } = useFacilityMenuItems();
  const { isMobileSession, isLoading: isLoadingSession } = useWebPunchSession();
  const allowedPathNamePrefix = compact(
    map(allMenuItems, (item) => getPrefix(item.link ?? ''))
  );
  const currentPathNamePrefix = getPrefix(pathname);

  if (!isLoadingSession && isMobileSession) {
    if (currentPathNamePrefix !== getPrefix(WEB_PUNCH_LINK)) {
      return <Navigate to={WEB_PUNCH_LINK} />;
    }
    return <Fragment>{children}</Fragment>;
  }

  if (
    !isLoading &&
    currentPathNamePrefix &&
    !MENU_EXEMPT_PATH_PREFIXES.includes(currentPathNamePrefix) &&
    !allowedPathNamePrefix.includes(currentPathNamePrefix)
  ) {
    return <Navigate to="/" />;
  }
  return <Fragment>{children}</Fragment>;
};

// Checks /Account/me before rendering Body so the menu/census/contact-support
// endpoints are never called while a support token has no facility context.
const SupportGate = ({ children }: PropsWithChildren) => {
  const { data, isLoading } = useAccountGet();

  if (isLoading) {
    return <OverlayLoader />;
  }

  if (data?.canEnterFacilities && !data?.isSupportSession) {
    return <FacilityPicker />;
  }

  return <Body>{children}</Body>;
};

const PrivateRouteGuard = ({ children }: PropsWithChildren) => {
  const { initialised, authenticated } = useAuth();

  if (!initialised) {
    return <OverlayLoader />;
  }

  if (initialised && !authenticated) {
    return <Navigate to="/auth/sign-in" />;
  }

  return <SupportGate>{children}</SupportGate>;
};

export default PrivateRouteGuard;
